// ─────────────────────────────────────────────────────────────────────
// champsTouches — LA DÉCLARATION DU GESTE HUMAIN
//
// La couche 2 de l'apprentissage (§2bis) ne capture une correction que
// si un humain a TOUCHÉ le champ. Ce fichier est l'endroit — le seul —
// où ce geste se déclare.
//
// Le verrou est celui du §11, ligne 11, et il a été payé : l'écran
// comparait la valeur du modèle à son propre état par défaut et
// appelait l'écart une décision humaine. « Forme du brut : box, lu
// cylinder, corrigé », sans que personne n'ait rien fait. Un signal
// d'apprentissage qui s'auto-alimente est PIRE qu'un signal absent : il
// monte, il rassure, et il ne mesure rien.
//
// D'où la règle, et elle décide de tout ce qui suit :
//
//   UNE CORRECTION SE DÉCLARE AU MOMENT DU GESTE.
//   Elle ne se déduit JAMAIS d'une comparaison de valeurs.
//
// Conséquence pratique : `declarer` est appelée depuis `patchPiece`, et
// `patchPiece` n'est atteint que par un `onChange` de champ ou un clic.
// Ce qui écrit une pièce sans geste humain — `applyEstimation`,
// `addPieceFromProposal` — passe par `setPieces` et ne déclare rien.
// Le garde-fou C.5 de `garde-affichage.test.js` refuse mécaniquement
// qu'un `onChange` parte d'un effet, ce qui rouvrirait le trou.
// ─────────────────────────────────────────────────────────────────────

/**
 * Les clés d'un patch, moins celles qui ne sont pas des champs.
 *
 * `champs_touches` s'exclut elle-même : elle est le journal du geste,
 * pas un champ de la pièce. Sans cette exclusion, la première
 * déclaration se déclarerait elle-même et le journal parlerait de lui.
 */
function clefsDuPatch(patch) {
  return Object.keys(patch ?? {}).filter((k) => k !== 'champs_touches');
}

/**
 * Rend la liste des champs touchés d'une pièce après application d'un
 * patch. Fonction PURE : elle se teste sans React et sans écran.
 *
 * Elle ACCUMULE. Un deviseur qui corrige la matière, enregistre plus
 * tard la quantité et enregistre encore doit voir ses deux corrections
 * capturées — et l'enregistrement remet le compteur à zéro tout seul,
 * puisque la pièce revient du serveur sans ce champ.
 */
export function declarer(pieceCourante, patch) {
  const deja = Array.isArray(pieceCourante?.champs_touches) ? pieceCourante.champs_touches : [];
  // Un appelant peut déclarer un champ qui n'est pas une clé du patch :
  // « appliquer la gamme révisée » écrit `gamme`, mais un bouton qui
  // n'écrirait rien d'autre qu'un effet de bord doit pouvoir le dire.
  const explicites = Array.isArray(patch?.champs_touches) ? patch.champs_touches : [];
  return [...new Set([...deja, ...explicites, ...clefsDuPatch(patch)])];
}

export default declarer;
